import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { User } from 'app/core/models/user.types';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import { CommonModule } from '@angular/common';
import { TSIconModule, TsButtonModule, TsOverlayModule } from 'tsui';
import { Approval, ApprovalStatus } from '../../../approvals/approvals.types';
import cloneDeep from 'lodash-es/cloneDeep';
import { UserState } from '@state/user/state';
import { Store } from '@ngxs/store';

@Component({
  selector: 'task-approval',
  styleUrls: ['./task-approval.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatCheckboxModule,
    MatDividerModule,
    MatIconModule,
    MatButtonToggleModule,
    TSIconModule,
    TsButtonModule,
    TsOverlayModule,
  ],
  template: `
    <div class="approval-requests">
      <div class="font-medium" *ngIf="label?.length && isFullLayout">{{ label }}</div>
      <mat-divider
        *ngIf="label?.length && isFullLayout"
        role="separator"
        class="mat-divider mt-1 mb-1 mat-divider-horizontal"
        aria-orientation="horizontal"
      >
      </mat-divider>
      <div class="flex flex-wrap gap-1 pt-1">
        <ts-button
          [type]="isFullLayout ? 'flat' : 'stroked'"
          [color]="isFullLayout ? 'basic' : 'accent'"
          icon="add"
          [tsOverlayTriggerFor]="overlayBox"
          [disabled]="!canEdit"
          [attr.data-testid]="'task-details-approval-add-button'"
          *ngIf="!viewMode"
        >
          Add
        </ts-button>

        <ts-overlay #overlayBox position="bottom">
          <div class="approval-requests__panels">
            <div class="approval-requests__panel-header">
              <div class="search-input">
                <ts-icon [icon]="'search'"></ts-icon>
                <input
                  type="text"
                  placeholder="Enter user name"
                  (input)="filtersUsers($event)"
                  [maxLength]="50"
                  [attr.data-testid]="'task-details-approval-user-input'"
                  #newTagInput
                />
              </div>
            </div>
            <mat-divider></mat-divider>
            <div class="approval-requests__panel-list available-tags">
              <ng-container>
                <mat-checkbox
                  *ngFor="let user of filteredUsers"
                  [color]="'primary'"
                  [checked]="requestedApprovalFor(user)"
                  (change)="toggleTaskApprover(user, $event.checked)"
                >
                  {{ user.name }}
                </mat-checkbox>
              </ng-container>
            </div>
          </div>
        </ts-overlay>

        <ng-container *ngIf="isFullLayout">
          <div class="request request--{{ request.status }}" *ngFor="let request of approvals">
            <div class="flex gap-2 hover:pointer">
              <span>{{ request.assigned_user.name }}</span>
              <ts-icon
                class="cursor-pointer"
                [icon]="'close'"
                *ngIf="canEdit && !viewMode && isPending(request)"
                (click)="toggleTaskApprover(request.assigned_user, false)"
              ></ts-icon>
              <ts-icon
                class="text-black-300"
                [icon]="'check_circle_outline'"
                *ngIf="viewMode && isPending(request)"
              ></ts-icon>
              <ts-icon class="text-green-500" [icon]="'check_circle'" *ngIf="isApproved(request)"></ts-icon>
              <ts-icon class="text-red-500" [icon]="'block'" *ngIf="isDeclined(request)"></ts-icon>
            </div>
          </div>
          <div class="flex text-black-500 gap-2" *ngIf="approvals?.length === 0 && viewMode">
            <ts-icon [icon]="'info'"></ts-icon>
            <p>No approvals requested.</p>
          </div>
        </ng-container>
      </div>
    </div>
  `,
})
export class TaskApprovalComponent extends OnDestroyMixin implements OnInit {
  private _approvals: Partial<Approval>[];

  @Input()
  set approvals(approvals: Partial<Approval>[]) {
    this._approvals = cloneDeep(approvals);
  }

  get approvals(): Partial<Approval>[] {
    return this._approvals;
  }

  get isFullLayout(): boolean {
    return this.layout === 'full';
  }

  @Input() viewMode = false;
  @Input() layout: 'full' | 'compact' = 'full';
  @Input() label = 'Approval needed';
  @Input() users?: User[];
  @Input() canEdit = true;
  @Output() addApprover: EventEmitter<Partial<Approval>> = new EventEmitter<Partial<Approval>>();
  @Output() removeApprover: EventEmitter<Partial<Approval>> = new EventEmitter<Partial<Approval>>();

  filteredUsers: User[];

  constructor(private store: Store) {
    super();
  }

  isPending(request: Approval): boolean {
    return request?.status === ApprovalStatus.NOT_YET || request?.status === ApprovalStatus.NOT_SENT;
  }

  isApproved(request: Approval): boolean {
    return request?.status === ApprovalStatus.APPROVED;
  }

  isDeclined(request: Approval): boolean {
    return request?.status === ApprovalStatus.DECLINED;
  }

  ngOnInit(): void {
    if (!this.users) {
      this.users = this.store.selectSnapshot(UserState.getUsers)?.filter((user) => !!user);
    }

    this.filteredUsers = this.users;
  }

  requestedApprovalFor(user: User): boolean {
    return !!this.approvals?.find((request) => request.assigned_user.id === user.id);
  }

  filtersUsers(event): void {
    const value = event.target.value.toLowerCase();
    this.filteredUsers = this.users.filter((user) => user.name.toLowerCase().includes(value));
  }

  toggleTaskApprover(user: User, add: boolean): void {
    if (this.viewMode) {
      return;
    }

    if (add) this.addApproverToTask(user);
    else this.deleteApproverFromTask(user);
  }

  addApproverToTask(user: User): void {
    const newRequest = {
      status: ApprovalStatus.NOT_YET,
      assigned_user: user,
    };

    if (!this.approvals) {
      this.approvals = [];
    }

    this.approvals.unshift(newRequest);
    this.addApprover.emit(newRequest);
  }

  deleteApproverFromTask(user: User): void {
    const indexToRemove = this.approvals.findIndex((item) => item.assigned_user.id === user.id);
    this.removeApprover.emit(this.approvals[indexToRemove]);
    this.approvals.splice(indexToRemove, 1);
  }
}
